<template>
  <b-container fluid class="login-container overflow-hidden p-0">
    <b-row no-gutters class="h-100">
      <b-col md="7" class="card-boas-vindas">
        <div :class="isDev ? 'bg-dev' : isHml ? 'bg-hml' : 'bg-prd'">
          <h1>Portal do TCE</h1>
          <h1>Protocolo Eletrônico</h1>
        </div>
      </b-col>

      <b-col
        sm="12"
        md="5"
        class="login-panel bg-white d-flex flex-column justify-content-between align-items-stretch"
      >
        <div class="header-area">
          <b-navbar type="light" variant="light">
            <b-navbar-nav class="navbar">
              <b-nav-item
                href="https://www.tce.se.gov.br/SitePages/jurisdicionado.aspx"
                class="linkDoc"
              >
                <font-awesome-icon icon="book" /> Documentação</b-nav-item
              >
              <span>|</span>
              <b-nav-item
                href="https://www.tce.se.gov.br/documentos/GuiaInterativoPortalTCE-SE.html"
                target="_blank"
                class="linkManual"
              >
                <font-awesome-icon icon="file-alt" /> Manual</b-nav-item
              >
              <span>|</span>
              <b-nav-item href="/cadastro" class="linkCadastro">
                <font-awesome-icon icon="address-card" />
                Cadastre-se</b-nav-item
              >
            </b-navbar-nav>
          </b-navbar>
          <b-img src="logo.png" height="180" alt="Logo TCE" class="my-5" />
        </div>

        <ConfirmaEmail
          v-if="confirmarEmail"
          @confirmado="confirmarEmail = false"
        />
        <template v-else>
          <LoginForm
            v-if="tipoLogin === 'form'"
            class="ml-4 mr-4"
            @dologin="handleLogin"
          />
          <LoginCert v-else @dologin="handleLogin" />

          <div class="outras-formas px-4 text-center">
            <template v-if="tipoLogin === 'form'">
              <div class="separador-formas my-3">
                <span>Outras formas de acesso</span>
              </div>
              <div class="d-flex flex-column align-items-center">
                <b-button
                  v-for="option in loginOptions"
                  :key="option.key"
                  variant="outline-secondary"
                  size="sm"
                  class="mb-2 login-option-btn"
                  @click="handleLoginOption(option.key)"
                >
                  <font-awesome-icon :icon="option.icon" class="mr-2" />
                  {{ option.label }}
                </b-button>
              </div>
            </template>
            <div v-else class="mt-3">
              <b-button
                variant="link"
                size="sm"
                class="link-voltar"
                @click="tipoLogin = 'form'"
              >
                <font-awesome-icon icon="arrow-left" class="mr-1" />
                Voltar para login de usuário
              </b-button>
            </div>
          </div>
        </template>

        <footer class="footer py-3 w-100">
          <p class="mb-0">
            <font-awesome-icon icon="envelope" class="mr-1" />
            <a
              class="link"
              href="mailto:integracao.jurisdicionado@tce.se.gov.br"
              >integracao.jurisdicionado@tce.se.gov.br</a
            >
          </p>
          <p class="mb-0">
            <font-awesome-icon icon="phone" class="mr-1" />
            <a class="link" href="tel:+557932164433">(79) 3216-4433</a>
          </p>
          <p class="mb-0">
            <span class="versao">v.{{ versaoApp }}</span>
          </p>
        </footer>
      </b-col>
    </b-row>
  </b-container>
</template>

<script lang="ts">
import { isDev, isHml, isNewUser, store, updateToken } from "@/store";
import { UtilsTS } from "@/utilsts";
import { mixins } from "vue-class-component";
import { Component, Vue } from "vue-property-decorator";
import LoginForm from "@/components/login/LoginForm.vue";
import LoginCert from "@/components/login/LoginCert.vue";
import { LoginData } from "@/types/administracao/usuarios/login-data";
import ConfirmaEmail from "@/components/login/ConfirmarEmail.vue";
import usuarioService from "@/services/administracao/usuarioService";

const CompProps = Vue.extend({
  name: "LoginComponent",
  components: {
    LoginForm,
    LoginCert,
    ConfirmaEmail,
  },
});

@Component
export default class LoginComponent extends mixins(UtilsTS, CompProps) {
  confirmarEmail = false;
  tipoLogin = "form";
  isDev = isDev();
  isHml = isHml();

  // Para adicionar novas formas de acesso, inclua um novo objeto neste array e trate a chave em handleLoginOption()
  loginOptions: Array<{ key: string; label: string; icon: string }> = [
    { key: "cert", label: "Login certificado digital", icon: "key" },
  ];

  get versaoApp(): string {
    return process.env.VUE_APP_VERSION || "?";
  }

  handleLoginOption(key: string): void {
    if (key === "cert") this.tipoLogin = "cert";
    // Adicione novos cases aqui conforme novas opções forem incluídas em loginOptions
  }

  mounted(): void {
    store.forceQuit = false;
  }

  async alertaReenvioAtivacao(): Promise<void> {
    this.$bvModal
      .msgBoxConfirm(`Deseja reativar seu usuário?`, {
        buttonSize: "sm",
        cancelTitle: "Cancelar",
        cancelVariant: "outline-secondary",
        centered: true,
        headerBgVariant: "secondary",
        headerTextVariant: "light",
        okTitle: "Confirmar",
        okVariant: "secondary",
        title: "Reativação de usuário",
      })
      .then(async (value) => {
        if (value) await this.reenviarAtivacao();
      })
      .catch((err) => {
        console.error(
          `Erro ao operar o dialog de alerta de reativação de usuário`,
          err
        );
      });
  }

  async handleLogin(ld: LoginData): Promise<void> {
    if (ld.termosPendentes && ld.termosPendentes.length)
      store.termoPendente = ld.termosPendentes[0];
    await updateToken(
      ld.token,
      ld.cpf,
      null,
      ld.nomeUsuario,
      ld.primeiroAcesso,
      ld.permissoes,
      null,
      ld.email,
      ld.nextStep,
      ld.idUsuario
    );
    if (isNewUser()) {
      switch (ld.situacao) {
        case "entregue":
          this.$router.push(`/confirma-email`);
          break;
        case "pendente dados": // campos liberados, podendo editar
        case "pendente validacao": // campos travados, não podendo editar
          this.$router.push(`/editar-cadastro`);
          break;
        default:
          if (ld.nextStep === "novo_usuario_certificado") {
            this.$router.push("/concluir-cadastro-token");
          } else {
            this.$toast.error(
              "Não foi possível identificar a situação do Usuário!"
            );
          }
      }
    } else {
      switch (ld.nextStep) {
        case "index":
          this.$router.push("/inicio");
          break;
        case "reativar-usuario":
          this.alertaReenvioAtivacao();
          break; // modal antes para confirmar reativação
        case "confirmar-contato":
          this.confirmarEmail = true;
          break;
        case "trocar-senha":
          this.$router.push("/seguranca-administracao/trocar-senha");
          break; // ver c passa
        default:
          this.$toast.error(
            "Não foi possível identificar a rota correta do Usuário!"
          );
      }
    }
  }
  async reenviarAtivacao(): Promise<void> {
    try {
      store.blockScreen = true;
      const response = await usuarioService.reenviarAtivacao();
      if (response) {
        this.confirmarEmail = true;
      } else {
        this.$toast.error("Erro ao reativar usuário");
      }
    } catch (error: unknown) {
      this.tratarError(error);
    } finally {
      store.blockScreen = false;
    }
  }
}
</script>

<style scoped>
.login-container {
  height: 100vh;
  overflow: hidden;
}

.login-container > .row {
  height: 100%;
}

.login-panel {
  max-height: 100vh;
  overflow-y: auto;
  overscroll-behavior: contain;
}

div.login-container {
  height: 100vh;
}

.bg-dev {
  background-image: url("../../assets/Curve-Line-Gray.svg");
  height: 100%;
  background-repeat: no-repeat;

  background-size: cover, contain;

  background-position: right, bottom;

  color: white;

  text-align: center;

  display: flex;

  justify-content: center;

  align-items: center;

  flex-direction: column;

  @media (max-width: 767px) {
    display: none;
  }
}

.bg-hml {
  background-image: url("../../assets/Curve-Line-Blue.svg");
  height: 100%;
  background-repeat: no-repeat;

  background-size: cover, contain;

  background-position: right, bottom;

  color: white;

  text-align: center;

  display: flex;

  justify-content: center;

  align-items: center;

  flex-direction: column;

  @media (max-width: 767px) {
    display: none;
  }
}

.bg-prd {
  background-image: url("../../assets/Curve-Line2.svg");
  height: 100%;
  background-repeat: no-repeat;

  background-size: cover, contain;

  background-position: right, bottom;

  color: white;

  text-align: center;

  display: flex;

  justify-content: center;

  align-items: center;

  flex-direction: column;
}

/* Esconde o painel verde no mobile */
@media (max-width: 767px) {
  .card-boas-vindas {
    display: none !important;
  }

  /* Ajusta o container branco para ocupar a tela sem rolagem */
  .bg-white.d-flex.flex-column.justify-content-between.align-items-stretch {
    justify-content: flex-start !important;
    min-height: 100vh !important;
    overflow-y: auto !important;
    padding-bottom: 0 !important;
  }

  /* Reduz o espaço da logo */
  .header-area img {
    height: 120px !important;
    margin: 1.5rem 0 1rem 0 !important;
  }

  /* Compacta o espaçamento interno */
  .header-area {
    margin-bottom: 0.5rem !important;
  }

  /* Deixa o formulário mais próximo da logo */
  .m-1 {
    margin-top: 0 !important;
  }

  /* Coloca o footer visível sem precisar rolar */
  .footer {
    margin-top: 0.5rem !important;
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
    position: static !important;
  }

  /* Deixa o texto do rodapé um pouco menor */
  .footer p {
    font-size: 0.85rem !important;
    margin-bottom: 0.25rem !important;
  }
}

.card-boas-vindas h1 {
  font-size: 4rem;
  font-optical-sizing: auto;
  font-weight: bold;
  font-style: normal;
}

.footer {
  bottom: 0;
  width: 100%;

  /* Cor clara */
  color: #6c757d;
  /* Texto cinza */
  font-size: 0.9rem;
}

.navbar {
  align-items: center;
  display: flex;
  font-size: 16px;
  justify-content: center;
  padding: 0.5rem 0.5rem;
}

li.nav-item.linkDoc a.nav-link {
  color: #007d47;
}
li.nav-item.linkManual a.nav-link {
  color: #ffc107;
}
li.nav-item.linkCadastro a.nav-link {
  color: #0069bf;
}

.separador-formas {
  display: flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.85rem;
}
.separador-formas::before,
.separador-formas::after {
  content: "";
  flex: 1;
  border-top: 1px solid #dee2e6;
}
.separador-formas::before {
  margin-right: 0.75rem;
}
.separador-formas::after {
  margin-left: 0.75rem;
}

.login-option-btn {
  width: 100%;
  max-width: 25rem;
}

.link-voltar {
  color: #6c757d;
  font-size: 0.9rem;
}

.link {
  color: #6c757d;
}

span.versao {
  color: var(--baseColor) !important;
}
</style>
